import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { DarkMode, Box } from '@podiumhq/design-system';

import useTrialPlan from 'components/Trials/hooks/useTrialPlan';
import useCheckAccountExists from 'components/Trials/hooks/useCheckAccountExists';
import useTrialTrackingData from 'components/Trials/hooks/useTrialTrackingData';
import { hasValidFields } from 'lib/fieldValidation';

export const TrialsContext = createContext({});

export const useTrialsContext = () => useContext(TrialsContext);

export const useTrialContextData = () => {
  const {
    checkEmailAddressExists,
    checkPhoneNumberExists,
    checkForInvite,
    emailExists,
    phoneExists,
    inviteExists,
    businessName,
  } = useCheckAccountExists();

  const trialTrackingData = useTrialTrackingData();
  const {
    loading: bundleLoading,
    term: trialPlanTerm,
    trialPlan,
  } = useTrialPlan() || {};

  const [accountExists, setAccountExists] = useState(false);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [intent, setIntent] = useState('');
  const [isSubmittingTrial, setIsSubmittingTrial] = useState(false);
  const [lastName, setLastName] = useState('');
  const [loginInfoErrors, setLoginInfoErrors] = useState({});
  const [nameErrors, setNameErrors] = useState({});
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [setupIntentData, setSetupIntentData] = useState(null);
  const [verifiedAccount, setVerifiedAccount] = useState(false);

  const hasValidLogin = useCallback(
    async (displayErrors, isPageView) => {
      const fieldsToValidate = isPageView
        ? { email, phone }
        : { email, password };
      const saveFields = displayErrors ? setLoginInfoErrors : null;
      const isValidLogin = hasValidFields(fieldsToValidate, saveFields);

      if (isValidLogin && isPageView) {
        const emailExists = await checkEmailAddressExists(email);
        const phoneExists = await checkPhoneNumberExists(phone);
        return isValidLogin && !emailExists && !phoneExists;
      } else if (isValidLogin) {
        const emailExists = await checkEmailAddressExists(email);
        return isValidLogin && !emailExists;
      } else {
        return isValidLogin;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [email, password, phone, trialTrackingData?.isRearrangeStepsFlow]
  );

  const hasValidName = useCallback(
    (displayErrors = true) => {
      const saveFields = displayErrors ? setNameErrors : null;

      return hasValidFields({ firstName, lastName }, saveFields);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [firstName, lastName]
  );

  useEffect(() => {
    setAccountExists(emailExists || phoneExists);
  }, [emailExists, phoneExists]);

  const accountInfoFieldProps = useMemo(
    () => ({
      errors: loginInfoErrors,
      setErrors: setLoginInfoErrors,
      trackingEventName: 'accountInfo',
    }),
    [loginInfoErrors]
  );

  const trialAccountData = useMemo(
    () => ({
      accountExists,
      accountInfoFieldProps,
      checkEmailAddressExists,
      checkPhoneNumberExists,
      checkForInvite,
      email,
      emailExists,
      firstName,
      lastName,
      password,
      phone,
      phoneExists,
      inviteExists,
      businessName,
      setAccountExists,
      setEmail,
      setFirstName,
      setLastName,
      setPassword,
      setPhone,
      setVerifiedAccount,
      verifiedAccount,
    }),
    [
      accountExists,
      accountInfoFieldProps,
      checkEmailAddressExists,
      checkPhoneNumberExists,
      checkForInvite,
      email,
      emailExists,
      inviteExists,
      businessName,
      firstName,
      phone,
      lastName,
      password,
      phoneExists,
      verifiedAccount,
    ]
  );

  const trialErrors = useMemo(
    () => ({
      loginInfoErrors,
      nameErrors,
      setLoginInfoErrors,
      setNameErrors,
    }),
    [loginInfoErrors, nameErrors]
  );

  const trialValidations = useMemo(
    () => ({ hasValidLogin, hasValidName }),
    [hasValidLogin, hasValidName]
  );

  return useMemo(
    () => ({
      bundleLoading,
      intent,
      isSubmittingTrial,
      setIntent,
      setIsSubmittingTrial,
      setSetupIntentData,
      setupIntentData,
      ...trialAccountData,
      ...trialErrors,
      trialPlan,
      trialPlanTerm,
      ...trialTrackingData,
      ...trialValidations,
    }),
    [
      bundleLoading,
      intent,
      isSubmittingTrial,
      setupIntentData,
      trialAccountData,
      trialErrors,
      trialPlan,
      trialPlanTerm,
      trialTrackingData,
      trialValidations,
    ]
  );
};

// easily pass & update user trials data info
const TrialsContextProvider = ({ children }) => {
  const trialData = useTrialContextData();

  if (trialData.loadingFlags)
    return (
      <DarkMode>
        <Box height="100vh" backgroundColor="black.base" />
      </DarkMode>
    );

  return (
    <TrialsContext.Provider value={trialData}>
      {children}
    </TrialsContext.Provider>
  );
};

export default TrialsContextProvider;
